import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TablePagination, Button, TextField, IconButton,
    Chip, Tooltip, InputAdornment, Collapse, Grid, Divider, Checkbox, Menu, MenuItem, ListItemIcon, ListItemText, Popper, Grow, ClickAwayListener,
    MenuList, Dialog, DialogTitle, DialogContent, DialogActions, Select, FormControl, CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    FileDownload as FileDownloadIcon,
    Person as PersonIcon,
    Upload as UploadIcon,
    PersonAdd as PersonAddIcon,
    PersonRemove as PersonRemoveIcon,
    ArrowRight as ArrowRightIcon,
    FilterList as FilterListIcon,
    MailOutline as MailOutlineIcon,
    Email,
} from '@mui/icons-material';
import { userService } from '../../services/api';
import UserFormModal from './UserFormModal';
import ResetPasswordModal from './ResetPasswordModal';
import StatusChangeModal from './StatusChangeModal';
import ImportUsersModal from './ImportUsersModal';
import { useToast } from '../../context/ToastContext';

import BulkDeleteModal from './BulkDeleteModal';
import { LoaderProvider, useLoader } from '../../context/LoaderContext';
import PageTabs from '../shared/PageTabs';
import { USERS_TABS } from '../shared/tabConfigs';
import StatusPill from '../shared/StatusPill';
import EmptyState from '../common/EmptyState';

const Users = () => {
    const { showToast } = useToast();
    const [users, setUsers] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(''); // Add status filter state
    const [permissions, setPermissions] = useState({
        canEdit: false,
        canDelete: false,
        canCreate: false
    });

    const [expandedRows, setExpandedRows] = useState({});
    const [userDetails, setUserDetails] = useState({});
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedUsersData, setSelectedUsersData] = useState({});
    // Add state for Add button menu
    const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
    const isAddMenuOpen = Boolean(addMenuAnchorEl);
    // Add state for Actions button menu
    const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
    const isActionsMenuOpen = Boolean(actionsMenuAnchorEl);

    // Add state for role assignment submenu
    const [assignRoleAnchorEl, setAssignRoleAnchorEl] = useState(null);
    const isAssignRoleMenuOpen = Boolean(assignRoleAnchorEl);

    // Add state for role removal submenu
    const [removeRoleAnchorEl, setRemoveRoleAnchorEl] = useState(null);
    const isRemoveRoleMenuOpen = Boolean(removeRoleAnchorEl);

    // Add state for roles
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);

    // Add state for confirmation dialog
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState('');
    const [selectedRole, setSelectedRole] = useState(null);

    // Modal states
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
    const [statusChangeModalOpen, setStatusChangeModalOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [statusAction, setStatusAction] = useState('');
    const [audienceFilter, setAudienceFilter] = useState('');
    const [confirmLoading, setConfirmLoading] = useState(false);
    const { startLoader, stopLoader } = useLoader();
    const [activeUserCount, setActiveUserCount] = useState(0);
    const [licenseCount, setLicenseCount] = useState(0);

    const [userLimitReached, setUserLimitReached] = useState(false);
    // Refs for hover menus
    const assignRoleRef = useRef(null);
    const removeRoleRef = useRef(null);

    // TODO: confirm this matches your actual API response shape.
    // Currently assumes user.audience === '2' means external (same convention
    // as the audienceFilter Select options below: '1' internal, '2' external).
    const EXTERNAL_AUDIENCE_VALUE = 2;
    const isExternalUser = (user) => {
        if (!user) return false;
        const value = user.audience_type;
        if (value === undefined || value === null) return false;
        if (typeof value === 'number') return value === 2;
        const normalized = String(value).toLowerCase();
        return normalized === '2' || normalized === 'external' || normalized === 'ext';
    };

    
    // TODO: confirm role shape — currently matches 'admin'/'author' against
    // role.name, role.key, or role.description, whichever is present.
    const PRIVILEGED_ROLE_KEYWORDS = ['admin', 'author'];
    const isPrivilegedRole = (role) => {
        if (!role) return false;
        const haystack = `${role.name || ''} ${role.key || ''} ${role.description || ''}`.toLowerCase();
        return PRIVILEGED_ROLE_KEYWORDS.some((keyword) => haystack.includes(keyword));
    };

    // Fetch users on component mount and when page, rowsPerPage, or searchTerm changes
    useEffect(() => {
        fetchUsers();
    }, [page, rowsPerPage, searchTerm, statusFilter, audienceFilter]);

    useEffect(() => {
        setSelectedUsers([]);
        setSelectedUsersData({});
    }, [page, rowsPerPage, searchTerm, statusFilter]);

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        try {
            setLoadingRoles(true);
            const response = await userService.getRoles();
            if (response.data.success) {
                setRoles(response.data.roles);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            showToast('Error fetching roles', 'error');
        } finally {
            setLoadingRoles(false);
        }
    };

    const fetchUsers = async () => {
        try {
            //setLoading(true);
            const response = await userService.getUsers(page + 1, rowsPerPage, searchTerm, statusFilter, audienceFilter);
            if (response.data.success) {
                setUsers(response.data.users);
                setTotalUsers(response.data.totalUsers);
                setPermissions(response.data.permissions);
                setUserLimitReached(response.data.userLimitReached || false);
                setActiveUserCount(response.data.activeUsers || 0);
                setLicenseCount(response.data.totalLicenseCount || 0);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            showToast('Error fetching users', 'error');
            setUsers([]);
        }
        // finally {
        //     setLoading(false);
        // }
    };

    const userFooterCalculation = licenseCount - activeUserCount;
    const isOverLimit = userFooterCalculation < 0;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        setExpandedRows({});
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        setExpandedRows({});
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
        setExpandedRows({});
    };

    // Add handler for status filter change
    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0);
        setExpandedRows({});
    };

    const handleAudienceChange = (e) => {
        setAudienceFilter(e.target.value);
        setPage(0);
        setExpandedRows({});
    };

    // const handleViewUser = async (userId) => {
    //     setExpandedRows(prev => ({
    //         ...prev,
    //         [userId]: !prev[userId]
    //     }));
    //     if (!expandedRows[userId] && !userDetails[userId]) {
    //         try {
    //             setUserDetails(prev => ({
    //                 ...prev,
    //                 [userId]: null // null indicates loading
    //             }));
    //             const response = await userService.getUserDetails(userId);
    //             if (response.data && response.data.userData) {
    //                 setUserDetails(prev => ({
    //                     ...prev,
    //                     [userId]: response.data.userData
    //                 }));
    //             }
    //         } catch (error) {
    //             console.error('Error fetching user details:', error);
    //             showToast('Error fetching user details', 'error');
    //         }
    //     }
    // };

    const handleSelectUser = (user) => {
        const userId = user.id;
        const isCurrentlySelected = selectedUsers.includes(userId);
        
        setSelectedUsers(prev =>
            isCurrentlySelected ? prev.filter(id => id !== userId) : [...prev, userId]
        );

        setSelectedUsersData(prev => {
            if (isCurrentlySelected) {
                const next = { ...prev };
                delete next[userId];
                return next;
            }
            return {
                ...prev,
                [userId]: {
                    id: user.id,
                    audience_type: user.audienceType,
                    active: user.active,
                },
            };
        });
    };

    const handleSelectAllUsers = (event) => {
        if (event.target.checked && users) {
            const currentPageUserIds = users.map(user => user.id);
            setSelectedUsers(currentPageUserIds);

            const dataMap = {};
            users.forEach(user => {
                dataMap[user.id] = {
                    id: user.id,
                    audience_type: user.audienceType,
                    active: user.active,
                };
            });
            setSelectedUsersData(dataMap);
        } else {
            setSelectedUsers([]);
            setSelectedUsersData({});
        }
    };

    const isAllSelected = users && users.length > 0 && selectedUsers.length === users.length;

    const hasExternalSelected = Object.values(selectedUsersData).some(isExternalUser);

    const handleAddMenuClick = (event) => {
        setAddMenuAnchorEl(event.currentTarget);
    };

    const handleAddMenuClose = () => {
        setAddMenuAnchorEl(null);
    };

    // Actions button menu handlers
    const handleActionsMenuClick = (event) => {
        setActionsMenuAnchorEl(event.currentTarget);
    };

    const handleActionsMenuClose = () => {
        setActionsMenuAnchorEl(null);
    };

    // Role assignment menu handlers
    const handleAssignRoleMenuOpen = (event) => {
        setAssignRoleAnchorEl(event.currentTarget);
        setRemoveRoleAnchorEl(null);
    };

    const handleAssignRoleMenuClose = () => {
        setAssignRoleAnchorEl(null);
    };

    // Role removal menu handlers
    const handleRemoveRoleMenuOpen = (event) => {
        setRemoveRoleAnchorEl(event.currentTarget);
        setAssignRoleAnchorEl(null);
    };

    const handleRemoveRoleMenuClose = () => {
        setRemoveRoleAnchorEl(null);
    };

    // Handle role assignment
    const handleAssignRole = async (roleId) => {
        const role = roles.find(r => r.id.toString() === roleId.toString());
        if (hasExternalSelected && isPrivilegedRole(role)) {
            showToast("External users can't hold admin or author roles", 'error');
            handleAssignRoleMenuClose();
            handleActionsMenuClose();
            return;
        }
        handleConfirmDialogOpen('assign', role);
    };

    // Handle role removal
    const handleRemoveRole = async (roleId) => {
        handleConfirmDialogOpen('remove', roles.find(r => r.id.toString() === roleId.toString()));
    };

    const handleConfirmDialogClose = () => {
        setConfirmDialogOpen(false);
    };

    const handleBulkResendInvite = () => {
        setConfirmAction('resend_invite');
        setConfirmDialogOpen(true);
    };

    const handleConfirmDialogOpen = (action, role) => {
        setConfirmAction(action);
        setSelectedRole(role);
        setConfirmDialogOpen(true);
        // Close the menus
        handleAssignRoleMenuClose();
        handleRemoveRoleMenuClose();
        handleActionsMenuClose();
    };
    const handleConfirmAction = async () => {
        try {
            handleActionsMenuClose();
            startLoader();
            setConfirmLoading(true);
            setConfirmDialogOpen(false);

            if (confirmAction === 'assign') {
                const response = await userService.assignRole(selectedUsers, selectedRole.id);
                if (response.data.success) {
                    const {
                        updatedCount = 0,
                        skippedCount = 0,
                        externalUserCount = 0
                    } = response.data;
                    //showToast(`Role "${selectedRole.description}" assigned successfully`, 'success');
                    //showToast(response.data.message || `Role "${selectedRole.description}" assigned successfully`, 'success');
                    if (updatedCount > 0) {
                        showToast(
                            `${updatedCount} user(s) assigned the "${selectedRole.description}" role successfully.`,
                            'success'
                        );
                    }

                    // Build skipped message
                    const skipped = [];

                    if (skippedCount > 0) {
                        showToast(`${skippedCount} already had this role`,'warning');
                    }

                    if (externalUserCount > 0) {
                        showToast(`${externalUserCount} external user(s) skipped`,'warning');
                    }

                    fetchUsers();
                } else {
                    showToast(response.data.message || 'Failed to assign role', 'error');
                }
            } else if (confirmAction === 'remove') {
                const response = await userService.removeRole(selectedUsers, selectedRole.id);
               
                if (response.data.success) {
                    showToast(`Role "${selectedRole.description}" removed successfully`, 'success');
                    fetchUsers();
                } else {
                    showToast(response.data.message || 'Failed to remove role', 'error');
                }
            } else if (confirmAction === 'resend_invite') {
                
                const inviteResp =  await userService.bulkResendInvite(selectedUsers);                
                if (inviteResp.data.success) {
                    showToast(inviteResp.data.message || 'Invitations resent successfully', 'success');                    
                } else {
                    showToast(inviteResp.data.message || 'Failed to Send invite', 'error');
                }
                setConfirmDialogOpen(false);
                
            }
        } catch (error) {
            console.error(`Error ${confirmAction}ing role:`, error);
            showToast(error.response?.data?.message || `Error ${confirmAction}ing role`, 'error');
        }
        finally {
            setConfirmLoading(false);
            stopLoader();
        }
    };

    const handleBulkDelete = () => {
        const selected = users.filter(user => selectedUsers.includes(user.id));
        const hasActiveUsers = selected.some(user => user.active === true);
        const hasInactiveUsers = selected.some(user => user.active === false);

        if (hasActiveUsers && hasInactiveUsers) {
            showToast("You can delete only inactive users. Please deselect active users.", "warning");
            return;
        }

        if (!hasInactiveUsers) {
            showToast("Only inactive users can be deleted", "warning");
            return;
        }

        const inactiveUserIds = selected
            .filter(user => user.active === false)
            .map(user => user.id);

        setSelectedUsers(inactiveUserIds);
        handleActionsMenuClose();
        setBulkDeleteModalOpen(true);
    };

    const handleCreateUser = () => {
        handleAddMenuClose();
        setSelectedUser(null);
        setFormModalOpen(true);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setFormModalOpen(true);
    };
    const handleUserFormSuccess = () => {
        fetchUsers();
        if (selectedUser) {
            setUserDetails(prev => {
                const newDetails = { ...prev };
                delete newDetails[selectedUser.id];
                return newDetails;
            });

            // Collapse the expanded row if it's open
            setExpandedRows(prev => {
                const newExpandedRows = { ...prev };
                if (newExpandedRows[selectedUser.id]) {
                    delete newExpandedRows[selectedUser.id];
                }
                return newExpandedRows;
            });
        }
    };

    const handleResetPassword = () => {
        handleActionsMenuClose();
        setResetPasswordModalOpen(true);

    };

    const handleStatusChange = (user, action) => {
        setSelectedUser(user);
        setStatusAction(action);
        setStatusChangeModalOpen(true);
    };

    const handleExportUsers = async () => {
        handleActionsMenuClose();

        try {
            let response;

            if (selectedUsers.length > 0) {
                // Export selected users
                response = await userService.exportSelectedUsers(selectedUsers);
            } else {
                // Export all users
                response = await userService.exportUserData();
            }

            const blob = new Blob([response.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            let filename = "users_export.csv";
            const contentDisposition = response.headers["content-disposition"];
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) filename = match[1];
            }

            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error exporting users:", error);
            showToast("Error exporting users", "error");
        }
    };

    const handleImportUsers = () => {
        handleAddMenuClose();
        setImportModalOpen(true);
    };

    const handleImportSuccess = () => {
        fetchUsers();
    };
    const renderUserDetails = (userId) => {
        const userData = userDetails[userId];
        if (!userData) {
            return null;
        }

        return (
            <Box sx={{ p: 2, borderRadius: 1 }}>
                <Grid container spacing={2} sx={{paddingLeft:'30px'}}>
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Username:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography variant="body2">{userData.username}</Typography>
                            </Grid>
                            {/* <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Gender:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography variant="body2">{userData.gender}</Typography>
                            </Grid> */}
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Job Role:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography variant="body2">{userData.job_role_name || 'Not assigned'}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Cohorts:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography variant="body2">
                                    {userData.groups && userData.groups.length > 0
                                        ? userData.groups.map(g => g.group_name).join(', ')
                                        : 'No cohorts'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={1}>
                            {/*
                            <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Roles:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography variant="body2">{userData.roles}</Typography>
                            </Grid>  */}
                            {/* <Grid item xs={4}>
                                <Typography variant="body2" color="text.secondary">Manager:</Typography>
                            </Grid> */}
                            {/* <Grid item xs={8}>
                                <Typography variant="body2">{userData.manager_name || 'None'}</Typography>
                            </Grid> */}

                        </Grid>
                    </Grid>
                    {userData.activeFields && userData.activeFields.length > 0 && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">Custom Fields</Typography>
                            <Divider sx={{ my: 1 }} />
                            <Grid container spacing={1}>
                                {userData.activeFields.map((field, index) => (
                                    <React.Fragment key={index}>
                                        <Grid item xs={4}>
                                            <Typography variant="body2" color="text.secondary">{field.field_name}:</Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body2">{field.field_values}</Typography>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            <PageTabs tabs={USERS_TABS} />
            <Typography variant="h4" gutterBottom className='page-title'>
                Users
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Name, Email, Role"
                        // className="custom-search-field"
                        className="pink-filter"
                        sx={{height: '35px'}}
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    
                    <FormControl sx={{ minWidth: 150, mr: 1 }}>
                        <Select
                        displayEmpty
                        value={audienceFilter}
                        onChange={handleAudienceChange}
                        className="pink-filter"
                        sx={{height: '35px'}}                        
                        inputProps={{ 'aria-label': 'Status filter' }}
                        >
                            <MenuItem value="">Both Audience</MenuItem>
                            <MenuItem value="1">Internal Audience</MenuItem>
                            <MenuItem value="2">External Audience</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" sx={{minWidth: 150,}}>
                        <Select
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            className="pink-filter"
                            sx={{height: '35px'}}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Status filter' }}
                            startAdornment={
                                <InputAdornment position="end">
                                    <FilterListIcon fontSize="small" />
                                </InputAdornment>
                            }
                        >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box display="flex">
                    <Box>
                        {selectedUsers.length > 0 && (
                            <Button
                                variant="outlined"
                                onClick={handleActionsMenuClick}
                                aria-controls={isActionsMenuOpen ? 'actions-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={isActionsMenuOpen ? 'true' : undefined}
                                sx={{ mr: 1 }}
                            >
                                Actions
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportUsers}
                            sx={{ mr: 1 }}
                        >
                            <b>Export Users</b>
                        </Button>

                        {permissions.canCreate && !userLimitReached && (
                            <>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddMenuClick}
                                    aria-controls={isAddMenuOpen ? 'add-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={isAddMenuOpen ? 'true' : undefined}
                                >
                                    <b>Add Users</b>
                                </Button>
                                <Menu
                                    id="add-menu"
                                    anchorEl={addMenuAnchorEl}
                                    open={isAddMenuOpen}
                                    onClose={handleAddMenuClose}
                                    MenuListProps={{
                                        'aria-labelledby': 'add-button',
                                    }}
                                >
                                    <MenuItem onClick={handleCreateUser}>
                                        <ListItemIcon>
                                            <PersonIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Single User</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={handleImportUsers}>
                                        <ListItemIcon>
                                            <UploadIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Upload CSV</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                    <Menu
                        id="actions-menu"
                        anchorEl={actionsMenuAnchorEl}
                        open={isActionsMenuOpen}
                        onClose={handleActionsMenuClose}
                        MenuListProps={{
                            'aria-labelledby': 'actions-button',
                        }}
                    >
                        {permissions.canEdit && (
                            <>
                                <MenuItem
                                    ref={assignRoleRef}
                                    onMouseEnter={handleAssignRoleMenuOpen}
                                    onClick={(e) => e.preventDefault()}
                                >
                                    <ListItemIcon>
                                        <PersonAddIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Assign Role</ListItemText>
                                    <ArrowRightIcon fontSize="small" />
                                </MenuItem>
                                <MenuItem
                                    ref={removeRoleRef}
                                    onMouseEnter={handleRemoveRoleMenuOpen}
                                    onClick={(e) => e.preventDefault()}
                                >
                                    <ListItemIcon>
                                        <PersonRemoveIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Remove Role</ListItemText>
                                    <ArrowRightIcon fontSize="small" />
                                </MenuItem>
                            </>
                        )}
                        {permissions.canDelete && (
                            <MenuItem onClick={handleBulkDelete}
                                onMouseEnter={() => {
                                    handleAssignRoleMenuClose();
                                    handleRemoveRoleMenuClose();
                                }}
                            >
                                <ListItemIcon>
                                    <DeleteIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Delete Users</ListItemText>
                            </MenuItem>
                        )}
                        {permissions.canEdit && (
                            <MenuItem onClick={handleBulkResendInvite}>
                                <ListItemIcon>
                                    <Email fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Resend Invite</ListItemText>
                            </MenuItem>
                        )}
                        {permissions.canEdit && (
                            <MenuItem onClick={handleResetPassword}
                            >
                                <ListItemIcon>
                                    <RefreshIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Reset Password</ListItemText>
                            </MenuItem>
                        )}
                    </Menu>
                    {/* Assign Role Submenu */}
                    <Popper
                        open={isAssignRoleMenuOpen}
                        anchorEl={assignRoleAnchorEl}
                        role={undefined}
                        placement="right-start"
                        transition
                        disablePortal
                        onMouseLeave={handleAssignRoleMenuClose}
                        sx={{ zIndex: 1301 }}
                    >
                        {({ TransitionProps }) => (
                            <Grow
                                {...TransitionProps}
                                style={{ transformOrigin: 'left top' }}
                            >
                                <Paper>
                                    <ClickAwayListener onClickAway={handleAssignRoleMenuClose}>
                                        <MenuList
                                            autoFocusItem={isAssignRoleMenuOpen}
                                            id="assign-role-menu"
                                        >
                                            {loadingRoles ? (
                                                <MenuItem disabled>
                                                    <ListItemText>Loading roles...</ListItemText>
                                                </MenuItem>
                                            ) : (
                                                roles
                                                    .filter(role => role.id !== 2)
                                                    .map((role) => {
                                                        const blocked = hasExternalSelected && isPrivilegedRole(role);
                                                        return (
                                                            <Tooltip
                                                                key={role.id}
                                                                title={blocked ? "External users can't hold Admin or Author roles" : ''}
                                                                disableHoverListener={!blocked}
                                                            >
                                                                <span>
                                                                    <MenuItem
                                                                        onClick={() => handleAssignRole(role.id)}
                                                                        disabled={blocked}
                                                                    >
                                                                        <ListItemText>Make {role.description}</ListItemText>
                                                                    </MenuItem>
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    })
                                            )}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>

                    {/* Remove Role Submenu */}
                    <Popper
                        open={isRemoveRoleMenuOpen}
                        anchorEl={removeRoleAnchorEl}
                        role={undefined}
                        placement="right-start"
                        transition
                        disablePortal
                        onMouseLeave={handleRemoveRoleMenuClose}
                        sx={{ zIndex: 1301 }}
                    >
                        {({ TransitionProps }) => (
                            <Grow
                                {...TransitionProps}
                                style={{ transformOrigin: 'left top' }}
                            >
                                <Paper>
                                    <ClickAwayListener onClickAway={handleRemoveRoleMenuClose}>
                                        <MenuList
                                            autoFocusItem={isRemoveRoleMenuOpen}
                                            id="remove-role-menu"
                                        >
                                            {loadingRoles ? (
                                                <MenuItem disabled>
                                                    <ListItemText>Loading roles...</ListItemText>
                                                </MenuItem>
                                            ) : (
                                                roles
                                                    .filter(role => role.id !== 2)
                                                    .map((role) => (
                                                        <MenuItem
                                                            key={role.id}
                                                            onClick={() => handleRemoveRole(role.id)}
                                                        >
                                                            <ListItemText>Remove {role.description}</ListItemText>
                                                        </MenuItem>
                                                    ))
                                            )}
                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </Box>
            </Box>

            {selectedUsers.length > 0 && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="white">
                        {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                    </Typography>
                </Box>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ width: '40px', pl: 0.5 }} >
                                <Checkbox
                                    indeterminate={selectedUsers.length > 0 && !isAllSelected}
                                    checked={isAllSelected}
                                    onChange={handleSelectAllUsers}
                                    className='table-checkboxes' size='small'
                                />
                            </TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>User Roles</TableCell>
                            {/* <TableCell>Cohorts</TableCell> */}
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users === null ? (
                            null // Don't show anything while loading
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14): a plain line. */}
                                    <EmptyState framed={false} title="No users found" message={null} />
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => (
                                <React.Fragment key={user.id}>
                                    <TableRow key={user.id}>
                                        <TableCell padding="checkbox" sx={{ width: '40px', pl: 0.5 }}>
                                            <Checkbox
                                                checked={selectedUsers.includes(user.id)}
                                                onChange={() => handleSelectUser(user)}
                                                className='table-checkboxes'
                                                size='small'
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {/* <Box
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: 'primary.main'
                                                }}
                                                onClick={() => handleViewUser(user.id)}
                                            >
                                                {user.name}
                                            </Box> */}
                                            {user.name}
                                        </TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>{user.roles.join(', ')}</TableCell>
                                        <TableCell>
                                            {permissions.canEdit ? (
                                                
                                                <StatusPill 
                                                    status={user.active} 
                                                    label={user.active === true ? 'Active' : user.active === 'invited' ? 'Invited' : 'Inactive'}
                                                    onClick={() => handleStatusChange(
                                                        user,
                                                        user.active === true ? 'deactivate' : user.active === false  ?  'activate' : 'resend_invite'
                                                    )}
                                                />
                                            ) : (

                                                <StatusPill 
                                                    status={user.active} 
                                                    label={user.active === true ? 'Active' : user.active === 'invited' ? 'Invited' : 'Inactive'}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                {/* <Tooltip title={expandedRows[user.id] ? "Hide Details" : "View Details"}>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleViewUser(user.id)}
                                                    >
                                                        {expandedRows[user.id] ? 
                                                            <VisibilityOffIcon fontSize="small" /> : 
                                                            <VisibilityIcon fontSize="small" />
                                                        }
                                                    </IconButton>
                                                </Tooltip> */}

                                                {permissions.canEdit && (
                                                    <>
                                                        <Tooltip title="Edit User">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditUser(user)}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {/* <Tooltip title="Reset Password">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleResetPassword(user)}
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip> */}
                                                    </>
                                                )}
                                                {/*
                                                {permissions.canDelete && (
                                                    <Tooltip title="Delete User">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleDeleteUser(user)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )} */}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {/* Expandable row for user details */}
                                    {expandedRows[user.id] && (
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                                                <Collapse in={expandedRows[user.id]} timeout="auto" unmountOnExit>
                                                    {renderUserDetails(user.id)}
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalUsers}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>{activeUserCount} active learners · </strong>

                <span
                    style={{
                    color: isOverLimit ? '#A32D2D' : 'inherit',
                    fontWeight: isOverLimit ? 600 : 400,
                    }}
                >
                    {userFooterCalculation}
                </span>

                {' '}of {licenseCount} licensed available
                </Typography>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleConfirmDialogClose}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">
                    {confirmAction === 'assign'
                        ? 'Assign Role'
                        : confirmAction === 'remove'
                        ? 'Remove Role'
                        : confirmAction === 'resend_invite'
                        ? 'Resend Invitation'
                        : 'Confirm Action'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        {confirmAction === 'assign'
                            ? `Are you sure you want to assign the role "${selectedRole?.description}" for the selected user${selectedUsers.length > 1 ? 's' : ''} (${selectedUsers.length})?`
                            : confirmAction === 'remove'
                            ? `Are you sure you want to remove the role "${selectedRole?.description}" for the selected user${selectedUsers.length > 1 ? 's' : ''} (${selectedUsers.length})?`
                            : confirmAction === 'resend_invite'
                            ? `Are you sure you want to resend the invitation to the selected user${selectedUsers.length > 1 ? 's' : ''} (${selectedUsers.length})?`
                            : 'Are you sure you want to perform this action?'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmDialogClose}>Cancel</Button>
                    <Button
                        onClick={handleConfirmAction}
                        variant="contained"
                        color="primary"
                        disabled={confirmLoading}
                        startIcon={confirmLoading ? <CircularProgress size={20} /> : null}
                    >
                        {confirmLoading ? 'Processing...' : 'Yes'}
                    </Button>
                </DialogActions>
            </Dialog>
            

            {/* Modals */}
            <UserFormModal
                open={formModalOpen}
                onClose={() => setFormModalOpen(false)}
                userId={selectedUser?.id}
                onSuccess={handleUserFormSuccess}
            />

            <ResetPasswordModal
                open={resetPasswordModalOpen}
                onClose={() => setResetPasswordModalOpen(false)}
                // userId={selectedUser?.id}
                userIds={selectedUsers}
                onSuccess={() => {
                    showToast('Password reset successfully', 'success');
                    setResetPasswordModalOpen(false);

                }}
            />

            <StatusChangeModal
                open={statusChangeModalOpen}
                onClose={() => setStatusChangeModalOpen(false)}
                userId={selectedUser?.id}
                action={statusAction}
                onSuccess={() => {
                    let message = 'User status updated successfully';
                    if (statusAction === 'resend_invite') {
                        message = 'Invitation resent successfully';
                    }
                    // if (statusAction === 'delete') {
                    //     message = 'User deleted successfully';
                    // }
                    showToast(message, 'success');
                    setStatusChangeModalOpen(false);
                    fetchUsers();
                }}
            />

            <ImportUsersModal
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                onSuccess={handleImportSuccess}
            />

            <BulkDeleteModal
                open={bulkDeleteModalOpen}
                onClose={() => setBulkDeleteModalOpen(false)}
                ids={selectedUsers}
                onSuccess={() => {
                    showToast('Users deleted successfully', 'success');
                    setBulkDeleteModalOpen(false);
                    fetchUsers();
                    setSelectedUsers([]);
                }}
                deleteFunction={userService.bulkDeleteUsers}
                title="Delete Users"
                itemName="user"
            />

        </Box>
    );
};

export default Users;